import type { Palette } from "./palette";

interface ItemTileProps {
  name: string;
  itemKey: string;
  palette: Palette;
  variant?: "card" | "hero";
}

const MOTIFS = ["split", "arc", "circle", "stripes", "rings", "stagger"] as const;

const hash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h >>> 0;
};

const getInitial = (name: string): string => {
  const match = (name || "").match(/[A-Za-zÀ-ÿ]/);
  return (match ? match[0] : (name || "•").charAt(0)).toUpperCase();
};

const ItemTile = ({ name, itemKey, palette, variant = "card" }: ItemTileProps) => {
  const h = hash(itemKey);
  const motif = MOTIFS[h % MOTIFS.length];
  const swap = (h >> 3) & 1;
  const a = swap ? palette.secondary : palette.primary;
  const b = swap ? palette.primary : palette.secondary;
  const surface = palette.surface;

  const hero = variant === "hero";
  const vbW = hero ? 200 : 100;
  const vbH = 100;
  const initial = getInitial(name);

  let composition: React.ReactNode;
  switch (motif) {
    case "split":
      composition = (
        <>
          <rect x={0} y={0} width={vbW} height={vbH} fill={a} />
          <polygon points={`${vbW},0 ${vbW},${vbH} 0,${vbH}`} fill={b} />
        </>
      );
      break;
    case "arc":
      composition = (
        <>
          <rect x={0} y={0} width={vbW} height={vbH} fill={a} />
          <circle cx={vbW} cy={0} r={vbH * 1.05} fill={b} />
        </>
      );
      break;
    case "circle":
      composition = (
        <>
          <rect x={0} y={0} width={vbW} height={vbH} fill={a} />
          <circle cx={vbW * 0.7} cy={vbH * 0.55} r={vbH * 0.45} fill={b} />
        </>
      );
      break;
    case "stripes":
      composition = (
        <>
          <rect x={0} y={0} width={vbW} height={vbH} fill={a} />
          {[0, 1, 2, 3].map((i) => (
            <rect
              key={i}
              x={i * (vbW / 4)}
              y={0}
              width={vbW / 8}
              height={vbH}
              fill={b}
              opacity={0.85}
            />
          ))}
        </>
      );
      break;
    case "rings":
      composition = (
        <>
          <rect x={0} y={0} width={vbW} height={vbH} fill={a} />
          <circle
            cx={vbW / 2}
            cy={vbH / 2}
            r={vbH * 0.42}
            fill="none"
            stroke={b}
            strokeWidth={vbH * 0.08}
          />
          <circle cx={vbW / 2} cy={vbH / 2} r={vbH * 0.22} fill={b} />
        </>
      );
      break;
    case "stagger":
    default:
      composition = (
        <>
          <rect x={0} y={0} width={vbW} height={vbH} fill={a} />
          <rect
            x={vbW * 0.1}
            y={vbH * 0.2}
            width={vbW * 0.5}
            height={vbH * 0.6}
            fill={b}
          />
          <rect
            x={vbW * 0.45}
            y={vbH * 0.1}
            width={vbW * 0.45}
            height={vbH * 0.55}
            fill={surface}
            opacity={0.7}
          />
        </>
      );
      break;
  }

  return (
    <svg
      className={`tile tile--${variant}`}
      viewBox={`0 0 ${vbW} ${vbH}`}
      preserveAspectRatio="xMidYMid slice"
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      {composition}
      <text
        x={hero ? vbW * 0.06 : vbW * 0.08}
        y={vbH * 0.88}
        fill={palette.ink}
        opacity={0.92}
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontSize: hero ? 56 : 60,
          fontWeight: 500,
          letterSpacing: "-0.02em",
        }}
      >
        {initial}
      </text>
    </svg>
  );
};

export default ItemTile;
